/** @graphql cc **/
import React, { useEffect, useMemo } from 'react';
import gql from 'graphql-tag';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	fetchUseCaseTemplatesByTempTypeAndUseCaseId,
	type Team,
	type UseCase,
} from '@atlassian/confluence-account-setup';

import { fg } from '@confluence/feature-gating';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import {
	USE_CASES_NTH_USER_ONBOARDING,
	NTH_USER_HOME_ONBOARDING_REVAMP_ONBOARDING_STATE_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';

import { usePTV2State } from '../usePTV2Store.tsx';
import type { BLUEPRINT_NAMES } from '../mappings/blueprintNameMapping.ts';
import {
	mapBlueprintModuleKeyToTemplate,
	BLUEPRINT_NAMES_SET,
	BLUEPRINT_NAME_TO_IDS_MAPPING,
} from '../mappings/blueprintNameMapping.ts';
import { teamToTemplatesMapping } from '../mappings/teamToTemplatesMapping.ts';
import {
	type WHITEBOARD_TEMPLATE_IDS,
	WHITEBOARD_TEMPLATE_IDS as WHITEBOARD_TEMPLATE_IDS_ENUM,
} from '../mappings/whiteboardMappings.ts';
import { DEFAULT_POPULAR_TEMPLATES } from '../mappings/defaultPopularTemplates.ts';
import { usePTV2TemplateUsage } from '../hooks/usePTV2TemplateUsage.tsx';
import { PTV2WhiteboardTemplateCard } from '../PTV2WhiteboardTemplateCard.tsx';
import { PTV2BlueprintTemplateCard } from '../PTV2BlueprintTemplateCard.tsx';

import { NthUserTemplateList } from './NthUserTemplateList.tsx';
import type {
	NthUserTemplatesAllTemplatesQuery,
	NthUserTemplatesAllTemplatesQueryVariables,
} from './__types__/NthUserTemplatesAllTemplatesQuery.ts';

const AllTemplatesQuery = gql`
	query NthUserTemplatesAllTemplatesQuery($usePersonalSpace: Boolean!) {
		allTemplates(usePersonalSpace: $usePersonalSpace) {
			nodes {
				templateId
				contentBlueprintId
				blueprintModuleCompleteKey
				itemModuleCompleteKey
				name
			}
		}
	}
`;

const SOURCE = 'nth-user-home-onboarding-revamp';

type NthUserTemplatesProps = {
	templateUsageValues: string | undefined;
	shouldShowUseCasesHomeWelcomeBanner?: boolean;
};

export const NthUserTemplates = ({
	templateUsageValues,
	shouldShowUseCasesHomeWelcomeBanner,
}: NthUserTemplatesProps) => {
	const { teamType, personalSpaceKey, onboardingPeriodDay, isTemplateListDismissed } =
		usePTV2State();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { data: allTemplatesData } = useQueryInterop<
		NthUserTemplatesAllTemplatesQuery,
		NthUserTemplatesAllTemplatesQueryVariables
	>(
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		AllTemplatesQuery,
		{
			variables: {
				usePersonalSpace: true,
			},
			skip: !personalSpaceKey || isTemplateListDismissed,
		},
	);

	const personalizationHeuristic = shouldShowUseCasesHomeWelcomeBanner
		? 'useCases'
		: teamToTemplatesMapping[teamType.toLocaleUpperCase()]
			? 'teamType'
			: 'popular';

	const blueprintModuleKeyToTemplate = mapBlueprintModuleKeyToTemplate(allTemplatesData);

	// Read use case selected templates from onboarding state
	const { data, loading, error } = useGetOnboardingState(
		[USE_CASES_NTH_USER_ONBOARDING.SELECTED_TEMPLATES],
		!fg('use-cases-onboarding-state-retrieval-kill-switch'),
	);

	// Determine needed templates
	const neededTemplates = useMemo(() => {
		let templates: Array<BLUEPRINT_NAMES | WHITEBOARD_TEMPLATE_IDS> = [];

		if (!loading && !error && fg('use-cases-onboarding-state-retrieval-kill-switch')) {
			const { selectedTemplates } = deserializeState(data);

			// Try to use use case selected templates if available
			if (shouldShowUseCasesHomeWelcomeBanner && selectedTemplates) {
				try {
					// Parse the stored use case template selection data
					const { teamType: selectedTeamType, useCases } = JSON.parse(selectedTemplates);

					// Fetch templates based on team type and use cases
					const useCaseTemplates = fetchUseCaseTemplatesByTempTypeAndUseCaseId(
						selectedTeamType as Team,
						useCases as UseCase[],
					);

					if (useCaseTemplates && useCaseTemplates.length > 0) {
						for (let uctIndex = 0; uctIndex < useCaseTemplates.length; uctIndex++) {
							if (useCaseTemplates[uctIndex].templateType === 'page') {
								for (const blueprintName of Object.keys(BLUEPRINT_NAME_TO_IDS_MAPPING)) {
									if (
										BLUEPRINT_NAME_TO_IDS_MAPPING[blueprintName as BLUEPRINT_NAMES]
											?.blueprintModuleCompleteKey == useCaseTemplates[uctIndex].templateId
									) {
										templates.push(blueprintName as BLUEPRINT_NAMES);
										break;
									}
								}
							} else {
								if (
									Object.values(WHITEBOARD_TEMPLATE_IDS_ENUM).includes(
										useCaseTemplates[uctIndex].templateId as WHITEBOARD_TEMPLATE_IDS_ENUM,
									)
								) {
									templates.push(useCaseTemplates[uctIndex].templateId as WHITEBOARD_TEMPLATE_IDS);
								}
							}
						}
					}
				} catch (error) {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'failed',
							actionSubject: 'useCaseTemplate',
							source: 'cc-experiment-use-cases-in-nth-user-onboarding',
							attributes: {
								errorMessage: error.message,
							},
						},
					}).fire();
				}
			}
		}

		// Fallback to team-based templates if no templates were set
		if (templates.length === 0) {
			templates = teamToTemplatesMapping[teamType.toLocaleUpperCase()] || DEFAULT_POPULAR_TEMPLATES;
		}

		return templates;
	}, [shouldShowUseCasesHomeWelcomeBanner, data, teamType, loading, error, createAnalyticsEvent]);

	const { deserializedTemplateUsage, incrementTemplateUsage } = usePTV2TemplateUsage(
		templateUsageValues,
		neededTemplates,
		NTH_USER_HOME_ONBOARDING_REVAMP_ONBOARDING_STATE_KEYS.templateUsageValues,
	);

	useEffect(() => {
		if (!isTemplateListDismissed) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'rendered',
					actionSubject: 'homeRevampTemplates',
					source: SOURCE,
					attributes: {
						personalizationHeuristic,
						onboardingPeriodDay,
					},
				},
			}).fire();
		}
	}, [
		createAnalyticsEvent,
		personalizationHeuristic,
		onboardingPeriodDay,
		isTemplateListDismissed,
	]);

	if (isTemplateListDismissed) {
		return null;
	}

	const cards: React.JSX.Element[] = neededTemplates.reduce(
		(current: React.JSX.Element[], templateName: BLUEPRINT_NAMES | WHITEBOARD_TEMPLATE_IDS) => {
			if (BLUEPRINT_NAMES_SET.has(templateName as BLUEPRINT_NAMES)) {
				return [
					...current,
					<PTV2BlueprintTemplateCard
						key={templateName}
						usage={deserializedTemplateUsage[templateName as BLUEPRINT_NAMES] || 0}
						onClick={() => {
							incrementTemplateUsage(templateName);
						}}
						templateName={templateName as BLUEPRINT_NAMES}
						blueprintModuleKeyToTemplate={blueprintModuleKeyToTemplate}
					/>,
				];
			} else {
				return [
					...current,
					<PTV2WhiteboardTemplateCard
						key={templateName}
						usage={deserializedTemplateUsage[templateName as WHITEBOARD_TEMPLATE_IDS] || 0}
						onClick={() => {
							incrementTemplateUsage(templateName);
						}}
						templateName={templateName as WHITEBOARD_TEMPLATE_IDS}
					/>,
				];
			}
		},
		[],
	);

	return (
		<NthUserTemplateList
			cards={cards}
			teamType={teamType}
			shouldShowUseCasesHomeWelcomeBanner={shouldShowUseCasesHomeWelcomeBanner}
		/>
	);
};
